import { useState, useCallback } from 'react';
import type { ProductVariation, MediaItem } from '@/types';

interface VariationManagerProps {
  variations: ProductVariation[];
  images: MediaItem[];
  onChange: (variations: ProductVariation[]) => void;
}

interface AttributeDef {
  name: string;
  values: string[];
}

function generateCombinations(attributes: AttributeDef[]): Record<string, string>[] {
  if (attributes.length === 0) return [];

  const result: Record<string, string>[] = [{}];

  for (const attr of attributes) {
    const newResult: Record<string, string>[] = [];
    for (const combo of result) {
      for (const value of attr.values) {
        newResult.push({ ...combo, [attr.name]: value });
      }
    }
    result.length = 0;
    result.push(...newResult);
  }

  return result;
}

export default function VariationManager({ variations, images, onChange }: VariationManagerProps) {
  const [attributes, setAttributes] = useState<AttributeDef[]>([
    { name: 'Color', values: [] },
    { name: 'Size', values: [] },
  ]);
  const [colorInput, setColorInput] = useState('');
  const [sizeInput, setSizeInput] = useState('');

  const addAttributeValue = useCallback(
    (attrIndex: number, value: string) => {
      if (!value.trim()) return;
      const updated = [...attributes];
      updated[attrIndex] = {
        ...updated[attrIndex],
        values: [...updated[attrIndex].values, value.trim()],
      };
      setAttributes(updated);
    },
    [attributes],
  );

  const removeAttributeValue = useCallback(
    (attrIndex: number, valueIndex: number) => {
      const updated = [...attributes];
      updated[attrIndex] = {
        ...updated[attrIndex],
        values: updated[attrIndex].values.filter((_, i) => i !== valueIndex),
      };
      setAttributes(updated);
    },
    [attributes],
  );

  const generateVariations = useCallback(() => {
    const combinations = generateCombinations(attributes);
    const newVariations: ProductVariation[] = combinations.map((combo, index) => {
      const existing = variations.find((v) =>
        Object.entries(combo).every(([key, val]) => v.attributes[key] === val),
      );
      return {
        id: existing?.id || crypto.randomUUID(),
        attributes: combo,
        sku: existing?.sku || `VAR-${index + 1}`,
        price: existing?.price || 0,
        stock: existing?.stock || 0,
        image: existing?.image,
      };
    });
    onChange(newVariations);
  }, [attributes, variations, onChange]);

  const updateVariation = useCallback(
    (index: number, field: keyof ProductVariation, value: ProductVariation[keyof ProductVariation]) => {
      const updated = [...variations];
      updated[index] = { ...updated[index], [field]: value };
      onChange(updated);
    },
    [variations, onChange],
  );

  const removeVariation = useCallback(
    (index: number) => {
      onChange(variations.filter((_, i) => i !== index));
    },
    [variations, onChange],
  );

  const clearVariations = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <div className="space-y-6">
      <div>
        <label className="label-field mb-2 block">Variation Attributes</label>
        <div className="space-y-4">
          {attributes.map((attr, attrIndex) => (
            <div key={attr.name} className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{attr.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...attributes];
                    updated.splice(attrIndex, 1);
                    setAttributes(updated);
                  }}
                  className="text-xs text-destructive hover:underline"
                >
                  Remove
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={attr.name === 'Color' ? colorInput : sizeInput}
                  onChange={(e) =>
                    attr.name === 'Color' ? setColorInput(e.target.value) : setSizeInput(e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = attr.name === 'Color' ? colorInput : sizeInput;
                      addAttributeValue(attrIndex, val);
                      attr.name === 'Color' ? setColorInput('') : setSizeInput('');
                    }
                  }}
                  placeholder={`Add ${attr.name.toLowerCase()} value`}
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = attr.name === 'Color' ? colorInput : sizeInput;
                    addAttributeValue(attrIndex, val);
                    attr.name === 'Color' ? setColorInput('') : setSizeInput('');
                  }}
                  className="btn-secondary"
                >
                  Add
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {attr.values.map((value, valIndex) => (
                  <span
                    key={valIndex}
                    className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground"
                  >
                    {value}
                    <button
                      type="button"
                      onClick={() => removeAttributeValue(attrIndex, valIndex)}
                      className="ml-1 text-muted-foreground hover:text-destructive"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button type="button" onClick={generateVariations} className="btn-primary">
          Generate Combinations
        </button>
        {variations.length > 0 && (
          <button type="button" onClick={clearVariations} className="btn-secondary">
            Clear All
          </button>
        )}
      </div>

      {variations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {attributes.map((attr) => (
                  <th key={attr.name} className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {attr.name}
                  </th>
                ))}
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">SKU</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Price (SGD)</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Stock</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {variations.map((variation, index) => (
                <tr key={variation.id} className="border-b border-border">
                  {attributes.map((attr) => (
                    <td key={attr.name} className="px-3 py-2">
                      {variation.attributes[attr.name] || '-'}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={variation.sku}
                      onChange={(e) => updateVariation(index, 'sku', e.target.value)}
                      className="input-field w-24"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={variation.price}
                      onChange={(e) => updateVariation(index, 'price', parseFloat(e.target.value) || 0)}
                      className="input-field w-24"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      value={variation.stock}
                      onChange={(e) => updateVariation(index, 'stock', parseInt(e.target.value) || 0)}
                      className="input-field w-20"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => removeVariation(index)}
                      className="text-destructive hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
